import "./send-docs-screen.css";

import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import type {
	MultiSelectChangeEvent,
	MultiSelectFilterChangeEvent,
} from "@tql/ui-components-react/kendo/dropdowns";
import type { FormRenderProps } from "@tql/ui-components-react/kendo/form";
import { Form, FormElement } from "@tql/ui-components-react/kendo/form";
import { Error, Label } from "@tql/ui-components-react/kendo/labels";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { MultiSelect } from "@tql/ui-components-react/tql/dropdowns";
import {
	arrowLeftLine,
	download_02Line,
	eyeLine,
	plusCircleLine,
} from "@tql/ui-components-react/untitled/icons";
import { useRef, useState } from "react";

import {
	FieldType,
	ValidationInputField,
} from "~/shared/components/fields/validation-input-field.tsx";
import { useNotifications } from "~/shared/hooks/use-notifications.ts";

import type { SendDocsFormData } from "../../hooks/use-tender-modal.tsx";

// RFC 5321 limits an address to 254 characters; cap entries to avoid abuse.
const MAX_EMAIL_LENGTH = 254;

// Lightweight email check matching the pattern used by the shipment
// notification modal: a single "@" that is not first/last and a "." after it.
const isValidEmail = (value: string): boolean => {
	const email = value.trim();
	if (!email || email.length > MAX_EMAIL_LENGTH) return false;
	const atIndex = email.indexOf("@");
	const hasSingleAt = atIndex > 0 && atIndex === email.lastIndexOf("@");
	if (!hasSingleAt || atIndex >= email.length - 1) return false;
	const domain = email.slice(atIndex + 1);
	return domain.includes(".") && !domain.endsWith(".");
};

const splitEmails = (value: string): string[] =>
	value
		.split(",")
		.map((email) => email.trim())
		.filter((email) => email.length > 0);

const getTrackingLinkFormData = (
	includeTrackingLink: boolean,
	readOnlyContent: boolean,
	usesPerShipmentTrackingLink: boolean,
): { includeTrackingLink?: boolean } =>
	readOnlyContent || usesPerShipmentTrackingLink ? { includeTrackingLink } : {};

const buildSendFormData = (
	dataItem: Record<string, unknown>,
	recipients: string[],
	includeTrackingLink: boolean,
	readOnlyContent: boolean,
	usesPerShipmentTrackingLink: boolean,
): SendDocsFormData => ({
	to: recipients.join(", "),
	subject: dataItem["subject"] as string,
	message: dataItem["message"] as string,
	...getTrackingLinkFormData(
		includeTrackingLink,
		readOnlyContent,
		usesPerShipmentTrackingLink,
	),
});

const getTrackingButtonLabel = (
	includeTrackingLink: boolean,
	readOnlyContent: boolean,
	usesPerShipmentTrackingLink: boolean,
): string => {
	if (usesPerShipmentTrackingLink) {
		return includeTrackingLink ? "Remove Tracking Links" : "Add Tracking Links";
	}
	if (readOnlyContent && includeTrackingLink) return "Remove Tracking Link";
	return "Add Tracking Link";
};

// Subjects map to an email header; cap the length to a sensible maximum.
const MAX_SUBJECT_LENGTH = 255;

// Front-end validation for the editable (internal) Subject field. The external
// read-only flow leaves the subject untouched, so it does not use this.
const subjectValidator = (value?: string) => {
	const subject = (value ?? "").trim();
	if (!subject) {
		return "Subject is required";
	}
	if (subject.length > MAX_SUBJECT_LENGTH) {
		return `Subject cannot exceed ${MAX_SUBJECT_LENGTH} characters`;
	}
	return "";
};

type SendDocsScreenProps = {
	formData: SendDocsFormData | null;
	onBackClick: () => void;
	onDownload?: () => void;
	onView?: () => void;
	isLoading?: boolean;
	trackingLinkMode?: "inline" | "per-shipment";
	/**
	 * When true, the subject and message fields are read-only (used for the
	 * external recipients flow). Recipients and the Add Tracking Link button
	 * remain interactive.
	 */
	readOnlyContent?: boolean;
	onFormDataChange: (data: SendDocsFormData | null) => void;
	sendDocsHook: {
		getInitialFormValues: () => {
			to: string;
			subject: string;
			message: string;
		};
		getTrackingLinkText: () => string;
		handleSubmit: (
			formData: SendDocsFormData,
			onSuccess?: () => void,
			onError?: (error: Error) => void,
		) => void;
		cancelEmail: () => void;
		isLoading: boolean;
	};
};

export const SendDocsScreen = ({
	formData,
	onBackClick,
	onDownload,
	onView,
	isLoading = false,
	readOnlyContent = false,
	trackingLinkMode = "inline",
	onFormDataChange,
	sendDocsHook,
}: SendDocsScreenProps) => {
	const { showSuccess, showError } = useNotifications();
	const {
		getInitialFormValues,
		getTrackingLinkText,
		handleSubmit,
		isLoading: isSending,
	} = sendDocsHook;

	// Use a ref to store the form render props so we can access it from outside the form
	const formRenderPropsRef = useRef<FormRenderProps | null>(null);

	const combinedIsLoading = isLoading || isSending;
	const usesPerShipmentTrackingLink = trackingLinkMode === "per-shipment";

	// Get the initial values - use persisted formData or default values
	const initialValues = formData ?? getInitialFormValues();

	// External recipients flow: the tracking link is sent as a boolean flag rather
	// than embedded text, so we track its toggle state here and mirror it in the
	// read-only message body as a preview.
	const [includeTrackingLink, setIncludeTrackingLink] = useState(
		formData?.includeTrackingLink ?? false,
	);

	// Recipients ("To") are captured as removable chips with per-email validation.
	// They are managed outside the Kendo Form (which only owns subject/message)
	// and serialized to a comma-separated string for the send hooks.
	const [recipients, setRecipients] = useState<string[]>(() =>
		splitEmails(initialValues.to),
	);
	const [recipientFilter, setRecipientFilter] = useState("");
	const [recipientError, setRecipientError] = useState("");

	// Add candidate emails to the recipient list — trimming, validating, and
	// de-duplicating (case-insensitive). Returns the next list and the first
	// invalid entry encountered, if any.
	const addEmails = (
		candidates: string[],
	): { next: string[]; firstInvalid?: string } => {
		let firstInvalid: string | undefined;
		const seen = new Set(recipients.map((email) => email.toLowerCase()));
		const next = [...recipients];
		for (const candidate of candidates) {
			const email = candidate.trim();
			if (!email) continue;
			if (!isValidEmail(email)) {
				firstInvalid ??= email;
				continue;
			}
			const key = email.toLowerCase();
			if (seen.has(key)) continue;
			seen.add(key);
			next.push(email);
		}
		return { next, firstInvalid };
	};

	const handleRecipientsChange = (event: MultiSelectChangeEvent) => {
		const incoming = event.value as string[];
		const added = incoming.filter((email) => !recipients.includes(email));

		// Chip removed or reordered — keep the resulting (already valid) list.
		if (added.length === 0) {
			setRecipients(incoming.filter((email) => isValidEmail(email)));
			setRecipientError("");
			return;
		}

		const { next, firstInvalid } = addEmails(added);
		setRecipients(next);
		setRecipientError(
			firstInvalid ? `"${firstInvalid}" is not a valid email address` : "",
		);
		setRecipientFilter("");
	};

	// Commit text typed but not yet turned into a chip (e.g. the user clicked Send
	// or moved focus without pressing Enter). Returns the resulting list.
	const commitPendingEmail = (): string[] => {
		const pending = recipientFilter.trim();
		if (!pending) return recipients;
		const { next, firstInvalid } = addEmails([pending]);
		if (firstInvalid) {
			setRecipientError(`"${firstInvalid}" is not a valid email address`);
			return recipients;
		}
		setRecipients(next);
		setRecipientFilter("");
		setRecipientError("");
		return next;
	};

	const onFormSubmit = (dataItem: Record<string, unknown>) => {
		const finalRecipients = commitPendingEmail();
		if (finalRecipients.length === 0) {
			setRecipientError("Add at least one recipient");
			return;
		}

		const formData = buildSendFormData(
			dataItem,
			finalRecipients,
			includeTrackingLink,
			readOnlyContent,
			usesPerShipmentTrackingLink,
		);

		handleSubmit(
			formData,
			() => {
				showSuccess("Email sent successfully");
				onBackClick();
			},
			(error: Error) => {
				showError(`Failed to send email: ${error.message}`);
			},
		);
	};

	const handleBackClick = () => {
		// Save current form state before navigating back
		if (formRenderPropsRef.current) {
			const currentFormData = buildSendFormData(
				{
					subject: formRenderPropsRef.current.valueGetter("subject") as string,
					message: formRenderPropsRef.current.valueGetter("message") as string,
				},
				recipients,
				includeTrackingLink,
				readOnlyContent,
				usesPerShipmentTrackingLink,
			);
			onFormDataChange(currentFormData);
		}
		onBackClick();
	};

	const handleCancelClick = () => {
		onBackClick();
	};

	return (
		<>
			<div className={"trax-modal-button back-button"}>
				<Button onClick={handleBackClick} variant={"tertiary"}>
					<SvgIcon icon={arrowLeftLine} size={"large"}></SvgIcon>
				</Button>
			</div>
			<Form
				ignoreModified={true}
				initialValues={initialValues}
				onSubmit={onFormSubmit}
				render={(formRenderProps: FormRenderProps) => {
					// Store form render props for access outside render function
					formRenderPropsRef.current = formRenderProps;

					const handleAddTrackingLink = () => {
						if (usesPerShipmentTrackingLink) {
							setIncludeTrackingLink((current) => !current);
							return;
						}

						const currentMessage =
							(formRenderProps.valueGetter("message") as string) || "";
						const trackingLinkText = getTrackingLinkText();

						// External flow: toggle the tracking-link flag and mirror it in the
						// read-only message preview. Internal flow keeps appending the link
						// directly into the editable message body.
						if (readOnlyContent) {
							if (includeTrackingLink) {
								formRenderProps.onChange("message", {
									value: currentMessage.replace(trackingLinkText, ""),
								});
								setIncludeTrackingLink(false);
							} else {
								formRenderProps.onChange("message", {
									value: currentMessage + trackingLinkText,
								});
								setIncludeTrackingLink(true);
							}
							return;
						}

						formRenderProps.onChange("message", {
							value: currentMessage + trackingLinkText,
						});
					};

					return (
						<FormElement>
							<div className={"tender-modal"}>
								<h3 className={"send-docs-header"}>
									Email BOL & Shipping Label
								</h3>
								<hr className={"tql-divider"} />
								<div className={"send-docs-field"}>
									<Label
										className={"k-form-label"}
										editorId={"to"}
										editorValid={!recipientError}
										id={"to_label"}
									>
										To&nbsp;<span className={"required"}>*</span>
									</Label>
									<div className={"k-form-field-wrap"}>
										<MultiSelect
											allowCustom={true}
											aria-describedby={recipientError ? "to_error" : undefined}
											aria-labelledby={"to_label"}
											autoClose={false}
											data={[]}
											disabled={combinedIsLoading}
											filter={recipientFilter}
											filterable={true}
											id={"to"}
											listNoDataRender={() => null}
											onBlur={() => commitPendingEmail()}
											onChange={handleRecipientsChange}
											onFilterChange={(event: MultiSelectFilterChangeEvent) =>
												setRecipientFilter(event.filter.value as string)
											}
											placeholder={"Type an email and press Enter"}
											tags={recipients.map((email) => ({
												text: email,
												data: [email],
											}))}
											valid={!recipientError}
											value={recipients}
										/>
										{recipientError ? (
											<Error id={"to_error"}>{recipientError}</Error>
										) : null}
									</div>
								</div>
								<div
									className={`send-docs-field${
										readOnlyContent ? " send-docs-field-readonly" : ""
									}`}
								>
									<ValidationInputField
										fieldType={FieldType.INPUT}
										id={"subject"}
										label={readOnlyContent ? "Subject (read-only)" : "Subject"}
										name={"subject"}
										readOnly={readOnlyContent}
										required={!readOnlyContent}
										validator={readOnlyContent ? undefined : subjectValidator}
									/>
								</div>
								<div
									className={`send-docs-field${
										readOnlyContent ? " send-docs-field-readonly" : ""
									}`}
								>
									<ValidationInputField
										fieldType={FieldType.TEXTAREA}
										id={"message"}
										label={readOnlyContent ? "Message (read-only)" : "Message"}
										name={"message"}
										readOnly={readOnlyContent}
										rows={5}
									/>
								</div>
								<div className={"send-docs-actions"}>
									<div>
										<Button
											disabled={combinedIsLoading}
											formAction={"none"}
											onClick={handleAddTrackingLink}
											svgIcon={plusCircleLine}
										>
											{getTrackingButtonLabel(
												includeTrackingLink,
												readOnlyContent,
												usesPerShipmentTrackingLink,
											)}
										</Button>
									</div>
									{onDownload ? (
										<div>
											<Button
												disabled={combinedIsLoading}
												formAction={"none"}
												onClick={onDownload}
												svgIcon={download_02Line}
											>
												Download
											</Button>
										</div>
									) : null}
									{onView ? (
										<div>
											<Button
												disabled={combinedIsLoading}
												formAction={"none"}
												onClick={onView}
												svgIcon={eyeLine}
											>
												View
											</Button>
										</div>
									) : null}
								</div>
								<hr className={"tql-divider"} />
								<div className={"send-docs-action-bar"}>
									<Button
										disabled={isSending}
										formAction={"submit"}
										variant={"primary"}
									>
										Send Email
									</Button>
									<Button
										formAction={"none"}
										onClick={handleCancelClick}
										variant={"secondary"}
									>
										Cancel
									</Button>
								</div>
							</div>
						</FormElement>
					);
				}}
			/>
		</>
	);
};
